import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TablePagination,
  IconButton,
  Card,
  CardContent,
  Typography,
  Box,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Chip,
  CircularProgress,
  Alert,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  Tabs,
  Tab,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  ListItemIcon,
  Divider,
} from '@mui/material';
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  Block as BlockIcon,
  CalendarToday as CalendarIcon,
  People as PeopleIcon,
  Euro as EuroIcon,
  CheckCircle as CheckCircleIcon,
  Schedule as ScheduleIcon,
  ViewList as ViewListIcon,
  CalendarMonth as CalendarMonthIcon,
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
  Dashboard as DashboardIcon,
  Warning as WarningIcon,
  TrendingUp as TrendingUpIcon,
  EventAvailable as EventAvailableIcon,
  HourglassEmpty as HourglassEmptyIcon,
} from '@mui/icons-material';
import { format, parseISO, startOfMonth, endOfMonth, eachDayOfInterval, isSameDay, addMonths, subMonths, startOfWeek, endOfWeek } from 'date-fns';
import { de } from 'date-fns/locale';
import SlotEditor from './SlotEditor';
import { CalendarSlot } from '../../../types/calendar.types';
import { API_BASE_URL } from '../../../config/api.config';

interface Statistics {
  totalSlots: number;
  confirmedSlots: number;
  averageCapacity: number;
  totalRevenue: number;
  totalBookings: number;
  blockedSlots: number;
}

interface FilterState {
  dateFrom: string;
  dateTo: string;
  tourType: string;
  status: string;
}

interface AdminCalendarDashboardProps {
  onCreateSlot?: () => void;
}

const AdminCalendarDashboard: React.FC<AdminCalendarDashboardProps> = ({ onCreateSlot }) => {
  const [slots, setSlots] = useState<CalendarSlot[]>([]);
  const [filteredSlots, setFilteredSlots] = useState<CalendarSlot[]>([]);
  const [statistics, setStatistics] = useState<Statistics | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedSlot, setSelectedSlot] = useState<CalendarSlot | null>(null);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [activeTab, setActiveTab] = useState(0); // 0 = Dashboard, 1 = Kalenderansicht, 2 = Listenansicht
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [filters, setFilters] = useState<FilterState>({
    dateFrom: format(new Date(), 'yyyy-MM-dd'),
    dateTo: format(new Date(Date.now() + 30 * 24 * 60 * 60 * 1000), 'yyyy-MM-dd'),
    tourType: 'all',
    status: 'all',
  });
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);

  // Fetch slots from API
  const loadSlots = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const token = localStorage.getItem('authToken');
      const url = `${API_BASE_URL}/Calendar/slots?from=${filters.dateFrom}&to=${filters.dateTo}`;
      const response = await fetch(url, {
        headers: {
          'Content-Type': 'application/json',
          'Authorization': token ? `Bearer ${token}` : '',
        },
      });

      if (!response.ok) {
        if (response.status === 404) {
          console.error('Calendar API endpoint not found (404)');
          throw new Error(`API Endpoint nicht gefunden (404). Bitte prüfen Sie die Backend-Konfiguration.`);
        }
        throw new Error(`HTTP ${response.status} ${response.statusText}`);
      }

      const data = await response.json();
      // CalendarController returns array of CalendarSlotDto directly
      const slotsArray = Array.isArray(data) ? data : (data.slots || data.Slots || []);
      setSlots(slotsArray);
      applyFilters(slotsArray);
    } catch (err) {
      console.error('Error loading slots:', err);
      setError('Fehler beim Laden der Termine. Bitte versuchen Sie es später erneut.');
    } finally {
      setLoading(false);
    }
  }, [filters.dateFrom, filters.dateTo]);

  // Calculate statistics with memoization
  const calculatedStats = useMemo(() => {
    if (!slots.length) return null;

    const stats: Statistics = {
      totalSlots: slots.length,
      confirmedSlots: slots.filter(s => s.isConfirmed).length,
      averageCapacity: slots.length > 0
        ? Math.round(slots.reduce((acc, s) => acc + s.capacityPercentage, 0) / slots.length)
        : 0,
      totalRevenue: slots.reduce((acc, s) => acc + (s.currentBookings * s.pricePerPerson), 0),
      totalBookings: slots.reduce((acc, s) => acc + s.currentBookings, 0),
      blockedSlots: slots.filter(s => s.isBlocked).length,
    };
    return stats;
  }, [slots]);

  // Keep calculateStatistics for compatibility
  const calculateStatistics = (slotsData: CalendarSlot[]) => {
    setStatistics(calculatedStats);
  };

  // Apply filters to slots with memoization
  const filteredSlotsData = useMemo(() => {
    let filtered = [...slots];

    // Filter by tour type
    if (filters.tourType !== 'all') {
      filtered = filtered.filter(s => s.tourType === filters.tourType);
    }

    // Filter by status
    if (filters.status === 'confirmed') {
      filtered = filtered.filter(s => s.isConfirmed);
    } else if (filters.status === 'unconfirmed') {
      filtered = filtered.filter(s => !s.isConfirmed);
    } else if (filters.status === 'blocked') {
      filtered = filtered.filter(s => s.isBlocked);
    }

    return filtered;
  }, [slots, filters.tourType, filters.status]);

  // Update applyFilters to use memoized data
  const applyFilters = useCallback((slotsData: CalendarSlot[]) => {
    // No need to recalculate, just use the memoized filteredSlotsData
    setFilteredSlots(filteredSlotsData);
  }, [filteredSlotsData]);

  // Handle filter changes
  const handleFilterChange = (key: keyof FilterState, value: string) => {
    setFilters(prev => ({ ...prev, [key]: value }));
    setPage(0); // Reset to first page when filter changes
  };

  // Handle page change
  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  // Handle rows per page change
  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Handle edit slot
  const handleEditSlot = (slot: CalendarSlot) => {
    setSelectedSlot(slot);
    setEditDialogOpen(true);
  };

  // Handle delete slot
  const handleDeleteClick = (slot: CalendarSlot) => {
    setSelectedSlot(slot);
    setDeleteDialogOpen(true);
  };

  // Confirm delete
  const handleDeleteConfirm = async () => {
    if (!selectedSlot) return;

    try {
      const token = localStorage.getItem('authToken');
      const response = await fetch(`${API_BASE_URL}/calendar/${selectedSlot.id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error('Fehler beim Löschen des Termins');
      }

      // Reload slots
      await loadSlots();
      setDeleteDialogOpen(false);
      setSelectedSlot(null);
    } catch (err) {
      console.error('Delete error:', err);
      setError('Fehler beim Löschen des Termins');
    }
  };

  // Handle block/unblock slot
  const handleToggleBlock = async (slot: CalendarSlot) => {
    try {
      const token = localStorage.getItem('authToken');
      const response = await fetch(`${API_BASE_URL}/calendar/${slot.id}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          ...slot,
          isBlocked: !slot.isBlocked,
        }),
      });

      if (!response.ok) {
        throw new Error('Fehler beim Blockieren/Freigeben des Termins');
      }

      // Reload slots
      await loadSlots();
    } catch (err) {
      console.error('Block toggle error:', err);
      setError('Fehler beim Blockieren/Freigeben des Termins');
    }
  };

  // Save edited slot
  const handleSaveSlot = async (updatedSlot: CalendarSlot) => {
    try {
      const token = localStorage.getItem('authToken');
      const response = await fetch(`${API_BASE_URL}/calendar/${updatedSlot.id}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(updatedSlot),
      });

      if (!response.ok) {
        throw new Error('Fehler beim Speichern des Termins');
      }

      // Reload slots
      await loadSlots();
      setEditDialogOpen(false);
      setSelectedSlot(null);
    } catch (err) {
      console.error('Save error:', err);
      setError('Fehler beim Speichern des Termins');
    }
  };

  // Load data on mount and filter change
  useEffect(() => {
    loadSlots();
  }, [loadSlots]);

  // Apply filters when they change
  useEffect(() => {
    applyFilters(slots);
  }, [filters.tourType, filters.status, slots]);

  // Get tour type color for chips (matching calendar view colors)
  const getTourTypeChipColor = (type: string): string => {
    switch (type.toUpperCase()) {
      case 'STANDARD': return '#1976d2'; // Blue
      case 'PREMIUM': return '#7b1fa2'; // Purple
      case 'ALL IN PREMIUM': return '#d81b60'; // Pink
      case 'EXKLUSIV': return '#f57c00'; // Orange
      default: return '#757575'; // Gray
    }
  };

  // Get capacity color
  const getCapacityColor = (percentage: number): 'error' | 'warning' | 'success' => {
    if (percentage < 20) return 'error';
    if (percentage < 50) return 'warning';
    return 'success';
  };

  // Get tour type background color with capacity shading
  const getTourTypeBackgroundColor = (tourType: string, capacityPercentage: number, isBlocked: boolean): string => {
    if (isBlocked) return '#f44336'; // Red for blocked

    const type = tourType.toUpperCase();

    // Base colors for tour types
    let baseColor: string;
    switch (type) {
      case 'STANDARD':
        // Blue shades
        if (capacityPercentage > 80) return '#1565c0'; // Dark blue
        if (capacityPercentage > 50) return '#1976d2'; // Medium blue
        return '#42a5f5'; // Light blue

      case 'PREMIUM':
        // Purple shades
        if (capacityPercentage > 80) return '#6a1b9a'; // Dark purple
        if (capacityPercentage > 50) return '#7b1fa2'; // Medium purple
        return '#9c27b0'; // Light purple

      case 'ALL IN PREMIUM':
        // Pink/Magenta shades
        if (capacityPercentage > 80) return '#c2185b'; // Dark pink
        if (capacityPercentage > 50) return '#d81b60'; // Medium pink
        return '#e91e63'; // Light pink

      case 'EXKLUSIV':
        // Gold/Orange shades
        if (capacityPercentage > 80) return '#e65100'; // Dark orange
        if (capacityPercentage > 50) return '#f57c00'; // Medium orange
        return '#ff9800'; // Light orange

      default:
        // Gray shades for unknown types
        if (capacityPercentage > 80) return '#616161'; // Dark gray
        if (capacityPercentage > 50) return '#757575'; // Medium gray
        return '#9e9e9e'; // Light gray
    }
  };

  // Get upcoming slots (next 5 slots)
  const upcomingSlots = useMemo(() => {
    const now = new Date();
    return filteredSlots
      .filter(slot => {
        const slotDate = parseISO(slot.date);
        return slotDate >= now;
      })
      .sort((a, b) => parseISO(a.date).getTime() - parseISO(b.date).getTime())
      .slice(0, 5);
  }, [filteredSlots]);

  // Get slots near minimum capacity
  const slotsNearMinCapacity = useMemo(() => {
    return filteredSlots
      .filter(slot => {
        const remaining = slot.minCapacity - slot.currentBookings;
        return remaining > 0 && remaining <= 5 && !slot.isBlocked;
      })
      .sort((a, b) => {
        const remainingA = a.minCapacity - a.currentBookings;
        const remainingB = b.minCapacity - b.currentBookings;
        return remainingA - remainingB;
      })
      .slice(0, 5);
  }, [filteredSlots]);

  // Get high capacity slots
  const highCapacitySlots = useMemo(() => {
    return filteredSlots
      .filter(slot => slot.capacityPercentage > 80 && !slot.isBlocked)
      .sort((a, b) => b.capacityPercentage - a.capacityPercentage)
      .slice(0, 5);
  }, [filteredSlots]);

  return (
    <Box sx={{ p: 3 }}>
      {/* Header with Title and Create Button */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" sx={{ display: 'flex', alignItems: 'center' }}>
          <CalendarIcon sx={{ mr: 1 }} />
          Kalender-Verwaltung
        </Typography>
        <Button
          variant="contained"
          startIcon={<CalendarIcon />}
          onClick={onCreateSlot}
        >
          + TERMINE ERSTELLEN
        </Button>
      </Box>

      {/* Tabs Navigation */}
      <Paper sx={{ mb: 3 }}>
        <Tabs
          value={activeTab}
          onChange={(e, newValue) => setActiveTab(newValue)}
          variant="fullWidth"
          indicatorColor="primary"
          textColor="primary"
        >
          <Tab icon={<DashboardIcon />} label="Dashboard" />
          <Tab icon={<CalendarMonthIcon />} label="Kalenderansicht" />
          <Tab icon={<ViewListIcon />} label="Listenansicht" />
        </Tabs>
      </Paper>

      {/* Filters */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <TextField
              label="Von Datum"
              type="date"
              value={filters.dateFrom}
              onChange={(e) => handleFilterChange('dateFrom', e.target.value)}
              fullWidth
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <TextField
              label="Bis Datum"
              type="date"
              value={filters.dateTo}
              onChange={(e) => handleFilterChange('dateTo', e.target.value)}
              fullWidth
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <FormControl fullWidth>
              <InputLabel>Tour-Typ</InputLabel>
              <Select
                value={filters.tourType}
                label="Tour-Typ"
                onChange={(e) => handleFilterChange('tourType', e.target.value)}
              >
                <MenuItem value="all">Alle</MenuItem>
                <MenuItem value="STANDARD">Standard</MenuItem>
                <MenuItem value="PREMIUM">Premium</MenuItem>
                <MenuItem value="ALL IN Premium">All In Premium</MenuItem>
                <MenuItem value="EXKLUSIV">Exklusiv</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <FormControl fullWidth>
              <InputLabel>Status</InputLabel>
              <Select
                value={filters.status}
                label="Status"
                onChange={(e) => handleFilterChange('status', e.target.value)}
              >
                <MenuItem value="all">Alle</MenuItem>
                <MenuItem value="confirmed">Bestätigt</MenuItem>
                <MenuItem value="unconfirmed">Unbestätigt</MenuItem>
                <MenuItem value="blocked">Blockiert</MenuItem>
              </Select>
            </FormControl>
          </Grid>
        </Grid>
      </Paper>

      {/* Error Alert */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Dashboard Tab */}
      {activeTab === 0 && (
        <>
          {/* Statistics Cards */}
          {statistics && (
            <Grid container spacing={3} sx={{ mb: 3 }}>
              <Grid size={{ xs: 12, sm: 6, md: 2.4 }}>
                <Card>
                  <CardContent>
                    <Typography color="textSecondary" gutterBottom>
                      <CalendarIcon /> Termine
                    </Typography>
                    <Typography variant="h4">{statistics.totalSlots}</Typography>
                    <Typography variant="body2" color="textSecondary">
                      {statistics.confirmedSlots} bestätigt
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid size={{ xs: 12, sm: 6, md: 2.4 }}>
                <Card>
                  <CardContent>
                    <Typography color="textSecondary" gutterBottom>
                      <PeopleIcon /> Auslastung
                    </Typography>
                    <Typography variant="h4">{statistics.averageCapacity}%</Typography>
                    <Typography variant="body2" color="textSecondary">
                      Durchschnitt
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid size={{ xs: 12, sm: 6, md: 2.4 }}>
                <Card>
                  <CardContent>
                    <Typography color="textSecondary" gutterBottom>
                      <EuroIcon /> Umsatz
                    </Typography>
                    <Typography variant="h4">
                      {new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' }).format(statistics.totalRevenue)}
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                      Gesamt
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid size={{ xs: 12, sm: 6, md: 2.4 }}>
                <Card>
                  <CardContent>
                    <Typography color="textSecondary" gutterBottom>
                      <CheckCircleIcon /> Buchungen
                    </Typography>
                    <Typography variant="h4">{statistics.totalBookings}</Typography>
                    <Typography variant="body2" color="textSecondary">
                      Teilnehmer
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid size={{ xs: 12, sm: 6, md: 2.4 }}>
                <Card>
                  <CardContent>
                    <Typography color="textSecondary" gutterBottom>
                      <BlockIcon /> Blockiert
                    </Typography>
                    <Typography variant="h4">{statistics.blockedSlots}</Typography>
                    <Typography variant="body2" color="textSecondary">
                      Termine
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          )}

          {/* Dashboard Content */}
          <Grid container spacing={3}>
            {/* Upcoming Slots */}
            <Grid size={{ xs: 12, md: 4 }}>
              <Paper sx={{ p: 2, height: '100%' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <EventAvailableIcon sx={{ mr: 1 }} color="primary" />
                  <Typography variant="h6">Nächste Termine</Typography>
                </Box>
                <Divider sx={{ mb: 2 }} />
                {upcomingSlots.length > 0 ? (
                  <List dense>
                    {upcomingSlots.map((slot) => (
                      <ListItem
                        key={slot.id}
                        disablePadding
                        sx={{
                          mb: 1,
                          borderLeft: `4px solid ${getTourTypeBackgroundColor(slot.tourType, slot.capacityPercentage, slot.isBlocked)}`,
                          backgroundColor: '#f5f5f5',
                          borderRadius: 1,
                        }}
                      >
                        <ListItemButton onClick={() => handleEditSlot(slot)}>
                          <ListItemText
                            primary={
                              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <Typography variant="body2" fontWeight="bold">
                                  {format(parseISO(slot.date), 'dd.MM.yyyy', { locale: de })}
                                </Typography>
                                <Chip label={slot.tourType} size="small" />
                              </Box>
                            }
                            secondary={
                              <>
                                <Typography variant="caption" display="block">
                                  {slot.displayTime} Uhr
                                </Typography>
                                <Typography variant="caption" display="block">
                                  {slot.currentBookings}/{slot.maxCapacity} Teilnehmer ({Math.round(slot.capacityPercentage)}%)
                                </Typography>
                              </>
                            }
                          />
                        </ListItemButton>
                      </ListItem>
                    ))}
                  </List>
                ) : (
                  <Typography variant="body2" color="textSecondary" align="center">
                    Keine anstehenden Termine
                  </Typography>
                )}
              </Paper>
            </Grid>

            {/* Slots near minimum capacity */}
            <Grid size={{ xs: 12, md: 4 }}>
              <Paper sx={{ p: 2, height: '100%' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <WarningIcon sx={{ mr: 1 }} color="warning" />
                  <Typography variant="h6">Mindestkapazität erforderlich</Typography>
                </Box>
                <Divider sx={{ mb: 2 }} />
                {slotsNearMinCapacity.length > 0 ? (
                  <List dense>
                    {slotsNearMinCapacity.map((slot) => {
                      const remaining = slot.minCapacity - slot.currentBookings;
                      return (
                        <ListItem
                          key={slot.id}
                          disablePadding
                          sx={{
                            mb: 1,
                            borderLeft: `4px solid #ff9800`,
                            backgroundColor: '#fff3e0',
                            borderRadius: 1,
                          }}
                        >
                          <ListItemButton onClick={() => handleEditSlot(slot)}>
                            <ListItemIcon>
                              <HourglassEmptyIcon color="warning" />
                            </ListItemIcon>
                            <ListItemText
                              primary={
                                <Typography variant="body2" fontWeight="bold">
                                  {format(parseISO(slot.date), 'dd.MM.yyyy', { locale: de })} - {slot.displayTime}
                                </Typography>
                              }
                              secondary={
                                <>
                                  <Typography variant="caption" display="block">
                                    {slot.tourType}
                                  </Typography>
                                  <Typography variant="caption" display="block" color="error">
                                    Noch {remaining} Teilnehmer erforderlich
                                  </Typography>
                                </>
                              }
                            />
                          </ListItemButton>
                        </ListItem>
                      );
                    })}
                  </List>
                ) : (
                  <Typography variant="body2" color="textSecondary" align="center">
                    Alle Termine erfüllen Mindestkapazität
                  </Typography>
                )}
              </Paper>
            </Grid>

            {/* High capacity slots */}
            <Grid size={{ xs: 12, md: 4 }}>
              <Paper sx={{ p: 2, height: '100%' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <TrendingUpIcon sx={{ mr: 1 }} color="success" />
                  <Typography variant="h6">Hohe Auslastung</Typography>
                </Box>
                <Divider sx={{ mb: 2 }} />
                {highCapacitySlots.length > 0 ? (
                  <List dense>
                    {highCapacitySlots.map((slot) => (
                      <ListItem
                        key={slot.id}
                        disablePadding
                        sx={{
                          mb: 1,
                          borderLeft: `4px solid #4caf50`,
                          backgroundColor: '#e8f5e9',
                          borderRadius: 1,
                        }}
                      >
                        <ListItemButton onClick={() => handleEditSlot(slot)}>
                          <ListItemText
                            primary={
                              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <Typography variant="body2" fontWeight="bold">
                                  {format(parseISO(slot.date), 'dd.MM.yyyy', { locale: de })}
                                </Typography>
                                <Chip
                                  label={`${Math.round(slot.capacityPercentage)}%`}
                                  size="small"
                                  color="success"
                                />
                              </Box>
                            }
                            secondary={
                              <>
                                <Typography variant="caption" display="block">
                                  {slot.displayTime} Uhr - {slot.tourType}
                                </Typography>
                                <Typography variant="caption" display="block">
                                  {slot.currentBookings}/{slot.maxCapacity} Teilnehmer
                                </Typography>
                              </>
                            }
                          />
                        </ListItemButton>
                      </ListItem>
                    ))}
                  </List>
                ) : (
                  <Typography variant="body2" color="textSecondary" align="center">
                    Keine Termine mit hoher Auslastung
                  </Typography>
                )}
              </Paper>
            </Grid>
          </Grid>
        </>
      )}

      {/* Calendar View Tab */}
      {activeTab === 1 && (
        /* Calendar Grid View */
        <Paper sx={{ p: 2 }}>
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
              <CircularProgress />
            </Box>
          ) : (
            <>
              {/* Month Navigation */}
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <IconButton onClick={() => setCurrentMonth(subMonths(currentMonth, 1))}>
                  <ChevronLeftIcon />
                </IconButton>
                <Typography variant="h5">
                  {format(currentMonth, 'MMMM yyyy', { locale: de })}
                </Typography>
                <IconButton onClick={() => setCurrentMonth(addMonths(currentMonth, 1))}>
                  <ChevronRightIcon />
                </IconButton>
              </Box>

              {/* Calendar Grid */}
              <Box sx={{
                display: 'grid',
                gridTemplateColumns: 'repeat(7, 1fr)',
                gap: 1,
                mb: 2
              }}>
                {/* Weekday Headers */}
                {['Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa', 'So'].map((day) => (
                  <Box key={day} sx={{
                    p: 1,
                    textAlign: 'center',
                    fontWeight: 'bold',
                    borderBottom: '2px solid #1976d2'
                  }}>
                    {day}
                  </Box>
                ))}

                {/* Calendar Days */}
                {(() => {
                  const monthStart = startOfMonth(currentMonth);
                  const monthEnd = endOfMonth(currentMonth);
                  const startDate = startOfWeek(monthStart, { weekStartsOn: 1 });
                  const endDate = endOfWeek(monthEnd, { weekStartsOn: 1 });
                  const days = eachDayOfInterval({ start: startDate, end: endDate });

                  return days.map((day) => {
                    const daySlots = filteredSlots.filter(slot =>
                      isSameDay(parseISO(slot.date), day)
                    );
                    const isCurrentMonth = day.getMonth() === currentMonth.getMonth();

                    return (
                      <Box
                        key={day.toString()}
                        sx={{
                          minHeight: '120px',
                          p: 1,
                          border: '1px solid #e0e0e0',
                          backgroundColor: isCurrentMonth ? '#fff' : '#f5f5f5',
                          opacity: isCurrentMonth ? 1 : 0.6,
                          display: 'flex',
                          flexDirection: 'column',
                        }}
                      >
                        <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 0.5 }}>
                          {format(day, 'd')}
                        </Typography>

                        {daySlots.length > 0 && (
                          <Box sx={{ flex: 1, overflow: 'auto' }}>
                            {daySlots.map((slot) => (
                              <Box
                                key={slot.id}
                                sx={{
                                  p: 0.5,
                                  mb: 0.5,
                                  fontSize: '0.75rem',
                                  backgroundColor: getTourTypeBackgroundColor(slot.tourType, slot.capacityPercentage, slot.isBlocked),
                                  color: 'white',
                                  borderRadius: 1,
                                  cursor: 'pointer',
                                  '&:hover': {
                                    opacity: 0.8,
                                  },
                                }}
                                onClick={() => handleEditSlot(slot)}
                                title={`${slot.displayTime} - ${slot.tourType}\n${slot.currentBookings}/${slot.maxCapacity} (${Math.round(slot.capacityPercentage)}%)`}
                              >
                                <Typography variant="caption" sx={{ display: 'block', fontWeight: 'bold', fontSize: '0.7rem' }}>
                                  {slot.displayTime}
                                </Typography>
                                <Typography variant="caption" sx={{ display: 'block', fontSize: '0.65rem', opacity: 0.9 }}>
                                  {slot.tourType}
                                </Typography>
                                <Typography variant="caption" sx={{ display: 'block', fontSize: '0.7rem' }}>
                                  {slot.currentBookings}/{slot.maxCapacity}
                                </Typography>
                              </Box>
                            ))}
                          </Box>
                        )}
                      </Box>
                    );
                  });
                })()}
              </Box>
            </>
          )}
        </Paper>
      )}

      {/* List View Tab */}
      {activeTab === 2 && (
        <Paper>
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
              <CircularProgress />
            </Box>
          ) : (
            <Table>
            <TableHead>
              <TableRow>
                <TableCell>Datum</TableCell>
                <TableCell>Zeit</TableCell>
                <TableCell>Tour-Typ</TableCell>
                <TableCell align="center">Kapazität</TableCell>
                <TableCell align="center">Auslastung</TableCell>
                <TableCell align="center">Status</TableCell>
                <TableCell align="right">Preis p.P.</TableCell>
                <TableCell align="center">Aktionen</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredSlots
                .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                .map((slot) => (
                <TableRow key={slot.id}>
                  <TableCell>
                    <Typography variant="body2">
                      {format(parseISO(slot.date), 'dd.MM.yyyy', { locale: de })}
                    </Typography>
                    {format(parseISO(slot.date), 'EEEE', { locale: de })}
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <ScheduleIcon fontSize="small" sx={{ mr: 1 }} />
                      {slot.displayTime}
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={slot.tourType}
                      size="small"
                      sx={{
                        backgroundColor: getTourTypeChipColor(slot.tourType),
                        color: 'white',
                        fontWeight: 'bold'
                      }}
                    />
                  </TableCell>
                  <TableCell align="center">
                    <Typography variant="body2">
                      {slot.currentBookings} / {slot.maxCapacity}
                    </Typography>
                    <Typography variant="caption" color="textSecondary">
                      Min: {slot.minCapacity}
                    </Typography>
                  </TableCell>
                  <TableCell align="center">
                    <Chip
                      label={`${Math.round(slot.capacityPercentage)}%`}
                      color={getCapacityColor(slot.capacityPercentage)}
                      size="small"
                    />
                  </TableCell>
                  <TableCell align="center">
                    {slot.isBlocked ? (
                      <Chip label="Blockiert" color="error" size="small" />
                    ) : slot.isConfirmed ? (
                      <Chip label="Bestätigt" color="success" size="small" />
                    ) : (
                      <Chip label="Offen" color="warning" size="small" />
                    )}
                  </TableCell>
                  <TableCell align="right">
                    {new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' }).format(slot.pricePerPerson)}
                  </TableCell>
                  <TableCell align="center">
                    <IconButton
                      size="small"
                      color="primary"
                      onClick={() => handleEditSlot(slot)}
                      title="Bearbeiten"
                    >
                      <EditIcon />
                    </IconButton>
                    <IconButton
                      size="small"
                      color={slot.isBlocked ? 'success' : 'warning'}
                      onClick={() => handleToggleBlock(slot)}
                      title={slot.isBlocked ? 'Freigeben' : 'Blockieren'}
                    >
                      <BlockIcon />
                    </IconButton>
                    <IconButton
                      size="small"
                      color="error"
                      onClick={() => handleDeleteClick(slot)}
                      title="Löschen"
                    >
                      <DeleteIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
              {filteredSlots.length === 0 && (
                <TableRow>
                  <TableCell colSpan={8} align="center">
                    <Typography variant="body2" color="textSecondary" sx={{ py: 3 }}>
                      Keine Termine gefunden
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
            </Table>
          )}
          {!loading && (
            <TablePagination
              rowsPerPageOptions={[10, 25, 50, 100]}
              component="div"
              count={filteredSlots.length}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              labelRowsPerPage="Einträge pro Seite:"
              labelDisplayedRows={({ from, to, count }) =>
                `${from}-${to} von ${count !== -1 ? count : `mehr als ${to}`}`
              }
            />
          )}
        </Paper>
      )}

      {/* Edit Dialog */}
      <SlotEditor
        slot={selectedSlot}
        open={editDialogOpen}
        onClose={() => {
          setEditDialogOpen(false);
          setSelectedSlot(null);
        }}
        onSave={handleSaveSlot}
      />

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Termin löschen</DialogTitle>
        <DialogContent>
          <Typography>
            Möchten Sie diesen Termin wirklich löschen?
          </Typography>
          {selectedSlot && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="body2">
                <strong>Datum:</strong> {format(parseISO(selectedSlot.date), 'dd.MM.yyyy', { locale: de })}
              </Typography>
              <Typography variant="body2">
                <strong>Zeit:</strong> {selectedSlot.displayTime}
              </Typography>
              <Typography variant="body2">
                <strong>Tour-Typ:</strong> {selectedSlot.tourType}
              </Typography>
              <Typography variant="body2" color="error">
                <strong>Aktuelle Buchungen:</strong> {selectedSlot.currentBookings} Teilnehmer
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Abbrechen</Button>
          <Button onClick={handleDeleteConfirm} color="error" variant="contained">
            Löschen
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AdminCalendarDashboard;